/**
 * Pages Contracts
 * Zod schemas for pages API endpoints.
 * STRICT BUT CORRECT: RAW schemas validate actual backend payloads exactly.
 * Normalized schemas are separate for frontend consumption.
 *
 * Runtime boundary: Backend (snake_case/tree_item_*) → RAW Schema validation → Normalizer → Frontend (camelCase)
 *
 * @module api/contracts/pages
 */
import { z } from "zod"

// LLM:METADATA
// :topology: [Editor SPA | API Contracts | Pages]
// :rationale: "Provides type-safe validation for Pages CRUD endpoints - enables contract-based testing and runtime validation"
// :contract: pre: "Valid input data for each operation", post: "Validated response matching backend contract"
// :references: SPEC: "Phase 3.1 Contract Completion"
// LLM:END

// ============================================================================
// RAW BACKEND SCHEMAS (snake_case/tree_item_* - STRICT validation)
// These schemas MUST match the exact backend response format
// Note: PHP mysqli returns integer IDs as strings - this is natural backend behavior
// ============================================================================

// RAW: /api/pages/fields response
export const RawPageFieldSchema = z.object({
  id: z.number().optional(),
  name: z.string().optional(),
  type: z.string(),
  label: z.string().nullable(),
  required: z
    .union([z.boolean(), z.number(), z.string()])
    .nullable()
    .optional(),
  default: z.unknown().nullable().optional(),
  options: z.unknown().nullable().optional(),
})

/**
 * RAW schema for /api/pages/fields.
 *
 * @precondition Backend returns either a legacy array of field descriptors or the
 * current object envelope keyed by field name under `fields`.
 * @postcondition Successful parses preserve the raw backend shape without
 * normalizing field names or coercing map entries into arrays.
 * @sideeffects None.
 * @throws ZodError when the backend response is neither a legacy array nor the
 * current keyed-object envelope.
 */
export const PagesFieldsResponseSchema = z.union([
  z.array(RawPageFieldSchema),
  z.object({
    fields: z.record(RawPageFieldSchema),
  }),
])

// RAW: /api/pages/list response (modern format - snake_case)
export const RawPageItemSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  slug: z.string().nullable(),
  active: z.number().nullable(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
})

// RAW: /api/pages/list (legacy tree_item_* format)
export const RawLegacyPageItemSchema = z.object({
  tree_item_id: z.number(),
  tree_item_title: z.string(),
  tree_item_uri: z.string(),
  tree_item_visible: z.number(),
  tree_item_level: z.number().optional(),
})

// RAW: Pages list response (union of both formats)
export const PagesListResponseSchema = z.array(
  z.union([RawPageItemSchema, RawLegacyPageItemSchema])
)

// RAW: Paginated pages list response
export const PagesListPaginatedResponseSchema = z.object({
  items: z.array(z.union([RawPageItemSchema, RawLegacyPageItemSchema])),
  total: z.number().optional(),
})

// RAW: /api/pages/list response can be either array or paginated object
export const PagesListAnyResponseSchema = z.union([
  PagesListResponseSchema,
  PagesListPaginatedResponseSchema,
])

// RAW: /api/pages/:id response (modern snake_case format)
export const RawPageSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string().nullable(),
  content: z.string().nullable(),
  meta_title: z.string().nullable().optional(),
  meta_description: z.string().nullable().optional(),
  active: z.number().nullable(),
  order_num: z.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
})

// RAW: /api/pages/:id response (legacy tree_item_* format)
export const RawLegacyPageSchema = z.object({
  tree_item_id: z.number().optional(),
  tree_item_title: z.string(),
  tree_item_uri: z.string(),
  tree_item_visible: z.number(),
  tree_item_window_title: z.string().nullable().optional(),
  tree_item_h1: z.string().nullable().optional(),
  tree_item_meta_keywords: z.string().nullable().optional(),
  tree_item_meta_description: z.string().nullable().optional(),
  tree_item_text: z.string().nullable().optional(),
})

// RAW: Single page response (union of both formats)
export const PageResponseSchema = z.union([RawPageSchema, RawLegacyPageSchema])

// Type exports for backward compatibility
export const PagesListResponse = PagesListResponseSchema
export const PagesListAnyResponse = PagesListAnyResponseSchema
export const PagesFieldsResponse = PagesFieldsResponseSchema
export const PageResponse = PageResponseSchema
export const PagesListPaginatedResponse = PagesListPaginatedResponseSchema

// RAW request schemas for validation (snake_case - match backend format)
export const RawPageCreateRequestSchema = z.object({
  name: z.string().min(1, "Name is required"),
  slug: z.string().optional(),
  content: z.string().optional(),
  meta_title: z.string().optional(),
  meta_description: z.string().optional(),
  active: z.number().optional(),
  order_num: z.number().optional(),
})

export const RawPageUpdateRequestSchema = RawPageCreateRequestSchema.partial()

// RAW delete response schema (matches backend format)
export const PageDeleteResponseSchema = z.object({
  result: z.string(),
  message: z.string().optional(),
})

// Combined contract object for easy import
export const pagesContract = {
  list: {
    schema: PagesListAnyResponseSchema,
    endpoint: "pages.list",
  },
  fields: {
    schema: PagesFieldsResponseSchema,
    endpoint: "pages.fields",
  },
  create: {
    request: RawPageCreateRequestSchema,
    response: PageResponseSchema,
    endpoint: "pages.create",
  },
  update: {
    request: RawPageUpdateRequestSchema,
    response: PageResponseSchema,
    endpoint: "pages.update",
  },
  delete: {
    schema: PageDeleteResponseSchema,
    endpoint: "pages.delete",
  },
}
